import React from 'react';
import { Box, Typography } from '@mui/material';
import { FP } from 'components/financepro';
import { fmtMontoContable } from './EstadoFinancieroShell';

export const LAYOUT_ESTADO_SIMPLE = {
  grid: { xs: '1fr auto', md: '118px 1fr auto' },
};

export const LAYOUT_BALANZA = {
  grid: {
    xs: 'minmax(148px, 1.5fr) repeat(4, minmax(92px, 1fr))',
    md: '108px minmax(170px, 1.6fr) repeat(4, minmax(96px, 1fr))',
  },
  minWidth: 720,
};

const esMonto = (col) => col.tipo === 'monto';
const esCuenta = (col) => col.tipo === 'cuenta';

const valorCelda = (fila, col) =>
  fila?.celdas && Object.prototype.hasOwnProperty.call(fila.celdas, col.id)
    ? fila.celdas[col.id]
    : fila?.[col.id];

const montoSx = (negativo, extra = {}) => ({
  fontVariantNumeric: 'tabular-nums',
  fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
  letterSpacing: '0.01em',
  color: negativo ? FP.color.danger : FP.color.textPrimary,
  whiteSpace: 'nowrap',
  textAlign: 'right',
  display: 'block',
  ...extra,
});

const colHeadSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: FP.color.textMuted,
};

const gridSx = (layout, extra = {}) => ({
  display: 'grid',
  gridTemplateColumns: layout?.grid || LAYOUT_ESTADO_SIMPLE.grid,
  columnGap: { xs: 1.5, md: 2 },
  ...extra,
});

const CeldaMonto = ({ valor, vacioSiCero = false, sx }) => {
  const n = parseFloat(valor) || 0;
  if (vacioSiCero && Math.abs(n) < 0.005) {
    return (
      <Typography component="span" sx={montoSx(false, { color: FP.color.textMuted, ...sx })}>
        —
      </Typography>
    );
  }
  return (
    <Typography component="span" sx={montoSx(n < 0, sx)}>
      ${fmtMontoContable(n)}
    </Typography>
  );
};

const EncabezadoDocumento = ({
  nombreEmpresa,
  tituloDocumento,
  periodo,
  kicker = 'Estados financieros',
  monedaNota = 'Cifras expresadas en moneda de la empresa',
}) => (
  <Box
    sx={{
      textAlign: 'center',
      px: { xs: 2.5, md: 5 },
      pt: { xs: 3, md: 4.5 },
      pb: 3,
      background: `linear-gradient(180deg, ${FP.color.navy} 0%, ${FP.color.navySoft} 100%)`,
      color: '#FFFFFF',
    }}
  >
    <Typography
      sx={{
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: '0.22em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.62)',
        mb: 1,
      }}
    >
      {kicker}
    </Typography>
    <Typography
      sx={{
        fontWeight: 800,
        fontSize: { xs: '1.15rem', md: '1.35rem' },
        letterSpacing: '0.04em',
        lineHeight: 1.25,
      }}
    >
      {nombreEmpresa}
    </Typography>
    <Box
      sx={{
        width: 56,
        height: 2,
        bgcolor: 'rgba(255,255,255,0.35)',
        mx: 'auto',
        my: 1.6,
      }}
    />
    <Typography
      sx={{
        fontWeight: 700,
        fontSize: { xs: '0.95rem', md: '1.05rem' },
        letterSpacing: '0.16em',
        textTransform: 'uppercase',
      }}
    >
      {tituloDocumento}
    </Typography>
    <Typography sx={{ mt: 1, fontSize: '0.86rem', color: 'rgba(255,255,255,0.82)', fontWeight: 500 }}>
      {periodo}
    </Typography>
    <Typography
      sx={{
        mt: 0.6,
        fontSize: '0.72rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.5)',
      }}
    >
      {monedaNota}
    </Typography>
  </Box>
);

const EncabezadoColumnas = ({ columnas, layout }) => (
  <Box sx={gridSx(layout, { alignItems: 'end', mb: 2.2, px: { xs: 0.5, md: 1 } })}>
    {columnas.map((col) => (
      <Typography
        key={col.id}
        sx={{
          ...colHeadSx,
          textAlign: esMonto(col) ? 'right' : 'left',
          display: col.ocultarEnMovil ? { xs: 'none', md: 'block' } : 'block',
        }}
      >
        {col.label}
      </Typography>
    ))}
  </Box>
);

const FilaCuenta = ({ fila, columnas, layout, indice }) => {
  const cuentaCol = columnas.find(esCuenta);
  const cuenta = cuentaCol ? fila.cuenta || valorCelda(fila, cuentaCol) : '';

  return (
    <Box
      sx={gridSx(layout, {
        alignItems: 'baseline',
        py: 0.85,
        px: { xs: 0.5, md: 1 },
        borderBottom: `1px solid ${FP.color.border}`,
        bgcolor: indice % 2 === 0 ? 'transparent' : FP.color.surface,
      })}
    >
      {columnas.map((col) => {
        if (esCuenta(col)) {
          return (
            <Typography
              key={col.id}
              sx={{
                display: { xs: 'none', md: 'block' },
                fontSize: '0.78rem',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                color: FP.color.textMuted,
                letterSpacing: '0.02em',
              }}
            >
              {cuenta || '—'}
            </Typography>
          );
        }
        if (!esMonto(col)) {
          return (
            <Box key={col.id} sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.88rem',
                  color: FP.color.textPrimary,
                  fontWeight: 500,
                  lineHeight: 1.35,
                }}
              >
                {fila.nombre || '—'}
              </Typography>
              {cuenta && cuentaCol?.ocultarEnMovil ? (
                <Typography
                  sx={{
                    display: { xs: 'block', md: 'none' },
                    fontSize: '0.7rem',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                    color: FP.color.textMuted,
                    mt: 0.15,
                  }}
                >
                  {cuenta}
                </Typography>
              ) : null}
            </Box>
          );
        }
        return (
          <CeldaMonto
            key={col.id}
            valor={valorCelda(fila, col)}
            vacioSiCero={Boolean(col.vacioSiCero)}
            sx={{ fontSize: '0.88rem', fontWeight: 600 }}
          />
        );
      })}
    </Box>
  );
};

const FilaTotal = ({ total, columnas, layout }) => {
  const variante = total.variante || 'seccion';
  const esIntermedio = variante === 'intermedio';
  const esNeto = variante === 'neto';
  const alerta = esNeto && total.tono === 'alerta';

  const colorEtiqueta = esNeto
    ? alerta
      ? FP.color.danger
      : FP.color.successDark
    : FP.color.navy;

  return (
    <Box
      sx={gridSx(layout, {
        alignItems: 'center',
        mt: esIntermedio ? 0.2 : esNeto ? 0.4 : 0,
        mb: esIntermedio ? 2.8 : 0,
        py: esNeto ? 1.5 : esIntermedio ? 1.15 : 1,
        px: esNeto ? 2 : { xs: 0.5, md: 1 },
        borderRadius: esNeto ? 1.4 : 0,
        borderTop: esNeto
          ? 'none'
          : esIntermedio
            ? `1px solid ${FP.color.borderStrong}`
            : `1px solid ${FP.color.navy}`,
        borderBottom: esNeto ? 'none' : esIntermedio ? `2px solid ${FP.color.navy}` : 'none',
        bgcolor: esNeto
          ? alerta
            ? FP.color.dangerSoft
            : FP.color.successSoft
          : esIntermedio
            ? FP.color.surfaceAlt
            : 'transparent',
      })}
    >
      {columnas.map((col) => {
        if (esCuenta(col)) {
          return (
            <Box
              key={col.id}
              sx={{ display: col.ocultarEnMovil ? { xs: 'none', md: 'block' } : 'block' }}
            />
          );
        }
        if (!esMonto(col)) {
          return (
            <Typography
              key={col.id}
              sx={{
                fontWeight: 800,
                fontSize: esNeto ? '0.82rem' : '0.8rem',
                letterSpacing: esNeto ? '0.12em' : '0.04em',
                textTransform: esNeto ? 'uppercase' : 'none',
                color: colorEtiqueta,
              }}
            >
              {total.etiqueta}
            </Typography>
          );
        }
        const n = parseFloat(valorCelda(total, col)) || 0;
        return (
          <Typography
            key={col.id}
            component="span"
            sx={montoSx(n < 0, {
              fontWeight: 800,
              fontSize: esNeto ? (columnas.filter(esMonto).length > 1 ? '1.02rem' : '1.08rem') : '0.92rem',
              color: esNeto
                ? alerta
                  ? FP.color.danger
                  : FP.color.successDark
                : n < 0
                  ? FP.color.danger
                  : FP.color.navy,
              borderBottom: esNeto ? '3px double currentColor' : 'none',
              pb: esNeto ? 0.15 : 0,
            })}
          >
            ${fmtMontoContable(n)}
          </Typography>
        );
      })}
    </Box>
  );
};

const SeccionEstado = ({ seccion, columnas, layout, mensajeVacio }) => {
  const filas = seccion.filas || seccion.cuentas || [];
  return (
    <Box sx={{ mb: 2.8 }}>
      <Box sx={{ mb: 1.1, pb: 0.7, borderBottom: `2px solid ${FP.color.navy}` }}>
        <Typography
          sx={{
            fontWeight: 800,
            fontSize: '0.78rem',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: FP.color.navy,
          }}
        >
          {seccion.titulo}
        </Typography>
        {seccion.subtitulo ? (
          <Typography sx={{ mt: 0.25, fontSize: '0.72rem', color: FP.color.textSecondary }}>
            {seccion.subtitulo}
          </Typography>
        ) : null}
      </Box>

      {filas.length === 0 ? (
        <Typography sx={{ py: 1.2, fontSize: '0.84rem', color: FP.color.textMuted, fontStyle: 'italic' }}>
          {seccion.mensajeVacio || mensajeVacio}
        </Typography>
      ) : (
        filas.map((fila, i) => (
          <FilaCuenta
            key={`${fila.cuenta || 'c'}-${i}`}
            fila={fila}
            columnas={columnas}
            layout={layout}
            indice={i}
          />
        ))
      )}

      {seccion.total ? (
        <FilaTotal total={seccion.total} columnas={columnas} layout={layout} />
      ) : null}

      {(seccion.despues || []).map((total, i) => (
        <FilaTotal
          key={`${total.etiqueta || 'd'}-${i}`}
          total={total}
          columnas={columnas}
          layout={layout}
        />
      ))}
    </Box>
  );
};

/**
 * Presentación genérica de estados financieros (pantalla).
 * El mismo modelo alimenta la plantilla de impresión.
 */
const PlantillaEstadoFinanciero = ({
  nombreEmpresa,
  tituloDocumento,
  periodo,
  kicker,
  monedaNota,
  columnas = [],
  secciones = [],
  totales = [],
  nota,
  layout = LAYOUT_ESTADO_SIMPLE,
  mensajeVacio = 'Sin movimientos en esta sección',
}) => (
  <Box sx={{ bgcolor: FP.color.surfaceAlt }}>
    <EncabezadoDocumento
      nombreEmpresa={nombreEmpresa}
      tituloDocumento={tituloDocumento}
      periodo={periodo}
      kicker={kicker}
      monedaNota={monedaNota}
    />
    <Box sx={{ px: { xs: 2, sm: 3, md: 5 }, py: { xs: 2.5, md: 3.5 }, overflowX: layout.minWidth ? 'auto' : 'visible' }}>
      <Box sx={{ minWidth: layout.minWidth || 0 }}>
        <EncabezadoColumnas columnas={columnas} layout={layout} />
        {secciones.map((seccion, idx) => (
          <SeccionEstado
            key={seccion.titulo || idx}
            seccion={seccion}
            columnas={columnas}
            layout={layout}
            mensajeVacio={mensajeVacio}
          />
        ))}
        {totales.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            {totales.map((total, i) => (
              <FilaTotal
                key={`${total.etiqueta || 't'}-${i}`}
                total={total}
                columnas={columnas}
                layout={layout}
              />
            ))}
          </Box>
        ) : null}
        {nota ? (
          <Typography
            sx={{
              mt: 3,
              fontSize: '0.7rem',
              color: FP.color.textMuted,
              textAlign: 'center',
              letterSpacing: '0.02em',
            }}
          >
            {nota}
          </Typography>
        ) : null}
      </Box>
    </Box>
  </Box>
);

export default PlantillaEstadoFinanciero;
